import { Auth0Provider, useAuth0 } from "@auth0/auth0-react";
import { useContext, useEffect, useRef, useState, type ReactNode } from "react";

import { auth0Config, checkLoggedIn, requireLogin, useLogin, getToken } from "./authConfig";
import { AuthIssueScreen } from "./components/AuthIssueScreen/AuthIssueScreen";
import { LoginContext } from "./loginContext";
import Layout from "./pages/layout/Layout";
import { useAuthClient } from "./useAuthClient";

type AuthFailureDetails = {
    title: string;
    message: string;
    details?: string;
    isRecoverable: boolean;
};

const ORGANIZATION_ERROR_HINTS = [
    "requires organization",
    "organization membership",
    "not a member of organization",
    "organization is required"
];

const isOrganizationAccessError = (value: string) => {
    const normalized = value.toLowerCase();
    return ORGANIZATION_ERROR_HINTS.some(hint => normalized.includes(hint));
};

const buildFailureDetails = (errorCode: string, errorDescription: string): AuthFailureDetails => {
    const details = errorDescription ? `Auth0 (${errorCode}): ${errorDescription}` : `Auth0 error: ${errorCode}`;

    if (isOrganizationAccessError(`${errorCode} ${errorDescription}`)) {
        return {
            title: "Unable to sign you in",
            message: "Your account is not registered for Atlas. Please contact your administrator for access.",
            details,
            isRecoverable: false
        };
    }

    return {
        title: "Authentication failed",
        message: "We could not complete sign-in. Please try again, or contact your administrator if this continues.",
        details,
        isRecoverable: true
    };
};

const parseAuthFailureFromLocation = (): AuthFailureDetails | null => {
    if (typeof window === "undefined") {
        return null;
    }

    const params = new URLSearchParams(window.location.search);
    const errorCode = params.get("error");
    if (!errorCode) {
        return null;
    }

    const errorDescription = params.get("error_description") ?? "";
    return buildFailureDetails(errorCode, errorDescription);
};

const parseAuthFailureFromSdkError = (error: unknown): AuthFailureDetails | null => {
    if (!error || typeof error !== "object") {
        return null;
    }
    const errorObj = error as { error?: string; error_description?: string; message?: string };
    const errorCode = errorObj.error ?? "auth_error";
    const errorDescription = errorObj.error_description ?? errorObj.message ?? "";
    return buildFailureDetails(errorCode, errorDescription);
};

const clearAuthErrorParamsFromUrl = () => {
    if (typeof window === "undefined") {
        return;
    }
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.delete("error");
    currentUrl.searchParams.delete("error_description");
    const nextSearch = currentUrl.searchParams.toString();
    const nextUrl = `${currentUrl.pathname}${nextSearch ? `?${nextSearch}` : ""}${currentUrl.hash}`;
    window.history.replaceState({}, document.title, nextUrl);
};

type Auth0StateSynchronizerProps = {
    setLoggedIn: (value: boolean) => void;
    children: ReactNode;
};

const Auth0StateSynchronizer = ({ setLoggedIn, children }: Auth0StateSynchronizerProps) => {
    const auth = useAuth0();
    const hasValidatedSessionRef = useRef(false);

    useEffect(() => {
        if (!auth.isLoading) {
            const sync = async () => {
                setLoggedIn(await checkLoggedIn(auth));
            };
            sync();
        }
    }, [auth.isAuthenticated, auth.isLoading]);

    useEffect(() => {
        if (auth.isLoading || hasValidatedSessionRef.current) {
            return;
        }

        if (auth.isAuthenticated && requireLogin) {
            hasValidatedSessionRef.current = true;
            console.log("[Auth] Validating session token on page load...");

            getToken(auth, { requireValidToken: true })
                .then(() => {
                    console.log("[Auth] Session token is valid");
                })
                .catch(() => {
                    console.log("[Auth] Session validation failed - redirect may have been triggered");
                });
        }
    }, [auth.isLoading, auth.isAuthenticated]);

    return <>{children}</>;
};

type ProtectedLayoutProps = {
    children: ReactNode;
};

const ProtectedLayout = ({ children }: ProtectedLayoutProps) => {
    const auth = useAuthClient();
    const { loggedIn } = useContext(LoginContext);
    const hasTriggeredLoginRef = useRef(false);
    const [urlAuthFailure, setUrlAuthFailure] = useState<AuthFailureDetails | null>(() => parseAuthFailureFromLocation());

    const isLoading = auth?.isLoading ?? false;
    const isAuthenticated = auth?.isAuthenticated ?? false;
    const sdkAuthFailure = parseAuthFailureFromSdkError(auth?.error);
    const authFailure = urlAuthFailure ?? sdkAuthFailure;

    useEffect(() => {
        if (!useLogin || !requireLogin || !auth) {
            return;
        }

        if (authFailure) {
            return;
        }

        if (isLoading) {
            return;
        }

        if (!isAuthenticated && !loggedIn) {
            console.log("[Auth] Redirecting to Auth0 login because user is not authenticated");
            if (!hasTriggeredLoginRef.current) {
                hasTriggeredLoginRef.current = true;
                const searchParams = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : undefined;
                const invitation = searchParams?.get("invitation");
                const organization = searchParams?.get("organization");

                if (invitation && organization) {
                    void auth.loginWithRedirect({
                        authorizationParams: {
                            invitation,
                            organization
                        }
                    });
                } else {
                    void auth.loginWithRedirect();
                }
            }
        }
    }, [auth, isLoading, isAuthenticated, loggedIn, authFailure]);

    const retrySignIn = () => {
        if (!auth) {
            return;
        }
        clearAuthErrorParamsFromUrl();
        setUrlAuthFailure(null);
        hasTriggeredLoginRef.current = true;
        void auth.loginWithRedirect();
    };

    if (authFailure) {
        return (
            <AuthIssueScreen
                title={authFailure.title}
                message={authFailure.message}
                details={authFailure.details}
                onRetry={authFailure.isRecoverable ? retrySignIn : undefined}
            />
        );
    }

    if (useLogin && requireLogin) {
        if (isLoading || (!isAuthenticated && !loggedIn)) {
            return null;
        }
    }

    return <>{children}</>;
};

const LayoutWrapper = () => {
    const [loggedIn, setLoggedIn] = useState(false);

    useEffect(() => {
        if (!useLogin) {
            checkLoggedIn(undefined).then(setLoggedIn);
        }
    }, []);

    if (useLogin) {
        if (!auth0Config.domain || !auth0Config.clientId) {
            throw new Error("Auth0 domain and clientId must be configured when login is enabled");
        }

        const redirectUri = window.location.origin;
        console.log("[Auth] Configuring Auth0Provider", {
            domain: auth0Config.domain,
            clientId: auth0Config.clientId,
            audience: auth0Config.audience,
            redirectUri
        });

        return (
            <Auth0Provider
                domain={auth0Config.domain}
                clientId={auth0Config.clientId}
                authorizationParams={{
                    redirect_uri: redirectUri,
                    scope: "openid profile email",
                    ...(auth0Config.audience ? { audience: auth0Config.audience } : {})
                }}
                cacheLocation="localstorage"
                useRefreshTokens={true}
                onRedirectCallback={appState => {
                    console.log("[Auth] Redirect callback completed", appState);
                    window.history.replaceState({}, document.title, appState?.returnTo || window.location.pathname);
                }}
            >
                <Auth0StateSynchronizer setLoggedIn={setLoggedIn}>
                    <LoginContext.Provider
                        value={{
                            loggedIn,
                            setLoggedIn
                        }}
                    >
                        <ProtectedLayout>
                            <Layout />
                        </ProtectedLayout>
                    </LoginContext.Provider>
                </Auth0StateSynchronizer>
            </Auth0Provider>
        );
    }

    return (
        <LoginContext.Provider
            value={{
                loggedIn,
                setLoggedIn
            }}
        >
            <ProtectedLayout>
                <Layout />
            </ProtectedLayout>
        </LoginContext.Provider>
    );
};

export default LayoutWrapper;
